import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useQuotes, QUOTE_STATUS_LABEL, QUOTE_STATUS_VARIANT } from "./api";
import { dateShort, money } from "@/lib/format";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { QueryError } from "@/components/shared/QueryError";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function QuotesPage() {
  const nav = useNavigate();
  const [status, setStatus] = useState("all");
  const { data: quotes, isLoading, error } = useQuotes({ status });
  return (
    <div>
      <PageHeader title="Devis" description="Chaque devis est figé à l'envoi ; pour négocier, on crée une nouvelle version." actions={<Button onClick={() => nav("/devis/nouveau")}><Plus className="mr-2 h-4 w-4" />Nouveau devis</Button>} />
      <div className="mb-4 flex items-center gap-2">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tous les statuts</SelectItem>{(Object.keys(QUOTE_STATUS_LABEL) as (keyof typeof QUOTE_STATUS_LABEL)[]).map((k) => <SelectItem key={k} value={k}>{QUOTE_STATUS_LABEL[k]}</SelectItem>)}</SelectContent>
        </Select>
        {quotes && <span className="ml-auto text-sm text-muted-foreground">{quotes.length} devis</span>}
      </div>
      {error ? <QueryError error={error} /> : isLoading ? <p className="text-sm text-muted-foreground">Chargement…</p> : !quotes?.length ? (
        <EmptyState title="Aucun devis" description="Crée un devis depuis un client : produits, remises, frais, acompte — tout se calcule en direct." action={<Button onClick={() => nav("/devis/nouveau")}>Créer le premier devis</Button>} />
      ) : (
        <div className="glass overflow-hidden rounded-2xl">
          <Table>
            <TableHeader><TableRow><TableHead>N°</TableHead><TableHead>Client</TableHead><TableHead>Objet</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
            <TableBody>
              {quotes.map((q) => (
                <TableRow key={q.id} className={`cursor-pointer ${q.superseded_by ? "opacity-60" : ""}`} onClick={() => nav(`/devis/${q.id}`)}>
                  <TableCell className="font-mono text-xs">{q.number ?? "brouillon"}{q.version > 1 ? ` v${q.version}` : ""}</TableCell>
                  <TableCell className="font-medium">{q.clients?.legal_name}</TableCell>
                  <TableCell className="text-muted-foreground">{q.title ?? "—"}</TableCell>
                  <TableCell>{dateShort(q.issue_date)}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(q.total, q.currency)}</TableCell>
                  <TableCell><Badge variant={QUOTE_STATUS_VARIANT[q.status]}>{QUOTE_STATUS_LABEL[q.status]}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
